import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import type { MultiSymbolAnalysisResponse, CorrelationData, SymbolStats } from '@/types/multi-symbol'

// Simple correlation heatmap component
function CorrelationHeatmap({ correlations }: { correlations: CorrelationData[] }) {
  const { t } = useTranslation()
  const symbols = useMemo(() => {
    const symbolSet = new Set<string>()
    correlations.forEach(corr => {
      symbolSet.add(corr.symbol1)
      symbolSet.add(corr.symbol2)
    })
    return Array.from(symbolSet).sort()
  }, [correlations])

  const getCorrelationValue = (symbol1: string, symbol2: string) => {
    if (symbol1 === symbol2) return 1
    const corr = correlations.find(c =>
      (c.symbol1 === symbol1 && c.symbol2 === symbol2) ||
      (c.symbol1 === symbol2 && c.symbol2 === symbol1)
    )
    return corr?.correlation || 0
  }

  const getCorrelationColor = (value: number) => {
    const absValue = Math.abs(value)
    if (absValue >= 0.7) return 'bg-red-500'
    if (absValue >= 0.5) return 'bg-orange-500'
    if (absValue >= 0.3) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getCorrelationIntensity = (value: number) => {
    const absValue = Math.abs(value)
    return Math.max(0.1, absValue) // Minimum opacity
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {/* Correlation Matrix */}
        <Card>
          <CardHeader>
            <CardTitle>{t('multiSymbol.correlationMatrix')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                <div className="grid gap-1" style={{
                  gridTemplateColumns: `repeat(${symbols.length + 1}, minmax(60px, 1fr))`
                }}>
                  {/* Header row */}
                  <div className="font-medium text-sm p-2"></div>
                  {symbols.map(symbol => (
                    <div key={symbol} className="font-medium text-sm p-2 text-center rotate-45 origin-center h-16 flex items-end justify-center">
                      {symbol}
                    </div>
                  ))}

                  {/* Data rows */}
                  {symbols.map(symbol1 => (
                    <React.Fragment key={symbol1}>
                      <div className="font-medium text-sm p-2 flex items-center">
                        {symbol1}
                      </div>
                      {symbols.map(symbol2 => {
                        const value = getCorrelationValue(symbol1, symbol2)
                        const intensity = getCorrelationIntensity(value)

                        return (
                          <div
                            key={`${symbol1}-${symbol2}`}
                            className={`p-2 text-center text-xs font-medium rounded ${getCorrelationColor(value)} text-white`}
                            style={{ opacity: intensity }}
                            title={`${symbol1} vs ${symbol2}: ${value.toFixed(3)}`}
                          >
                            {symbol1 === symbol2 ? '1.000' : value.toFixed(3)}
                          </div>
                        )
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Correlations List */}
        <Card>
          <CardHeader>
            <CardTitle>{t('multiSymbol.topCorrelations')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {correlations
                .sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation))
                .slice(0, 10)
                .map((corr, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${getCorrelationColor(corr.correlation)}`} />
                      <span className="font-medium">
                        {corr.symbol1} ↔ {corr.symbol2}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        corr.strength === 'strong' ? 'destructive' :
                        corr.strength === 'moderate' ? 'default' : 'secondary'
                      }>
                        {corr.strength}
                      </Badge>
                      <span className="font-mono text-sm">
                        {corr.correlation.toFixed(3)}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// Performance ranking component
function PerformanceRanking({ bestPerformers }: { bestPerformers: any[] }) {
  const { t } = useTranslation()
  const metrics = useMemo(() => {
    const metricSet = new Set(bestPerformers.map(p => p.metric))
    return Array.from(metricSet)
  }, [bestPerformers])

  return (
    <div className="space-y-4">
      {metrics.map(metric => {
        const metricPerformers = bestPerformers
          .filter(p => p.metric === metric)
          .sort((a, b) => a.rank - b.rank)
          .slice(0, 10)

        return (
          <Card key={metric}>
            <CardHeader>
              <CardTitle className="capitalize">
                {metric.replace(/_/g, ' ')} {t('multiSymbol.ranking')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {metricPerformers.map((performer) => (
                  <div key={`${performer.symbol}-${metric}`} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full font-bold text-sm">
                        {performer.rank}
                      </div>
                      <span className="font-medium">{performer.symbol}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-mono">
                        {typeof performer.value === 'number'
                          ? performer.value.toFixed(4)
                          : performer.value
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

// Symbol statistics component
function SymbolStatistics({ symbolStats, metadata }: {
  symbolStats: Record<string, SymbolStats>
  metadata: MultiSymbolAnalysisResponse['metadata']
}) {
  const { t } = useTranslation()
  const statsArray = Object.values(symbolStats)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('multiSymbol.symbolStatistics')}</CardTitle>
        <div className="text-sm text-muted-foreground">
          {t('multiSymbol.analysisOfSymbols', { count: metadata.symbolsAnalyzed })}
        </div>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-4 gap-4 p-4 font-medium border-b bg-muted">
            <div>{t('multiSymbol.symbolLabel')}</div>
            <div>{t('multiSymbol.records')}</div>
            <div>{t('multiSymbol.hasData')}</div>
            <div>{t('multiSymbol.dataset')}</div>
          </div>
          {statsArray.map((stat) => (
            <div key={stat.symbol} className="grid grid-cols-4 gap-4 p-4 border-b">
              <div className="font-medium">{stat.symbol}</div>
              <div>{stat.resultCount || 0}</div>
              <div>
                <Badge variant={stat.hasData ? "default" : "secondary"}>
                  {stat.hasData ? t('common.yes') : t('common.no')}
                </Badge>
              </div>
              <div className="font-mono text-xs">
                {stat.datasetId?.slice(0, 8)}...
              </div>
            </div>
          ))}
        </div>

        {/* Additional stats for symbols with data */}
        {statsArray.filter(stat => stat.hasData).length > 0 && (
          <div className="mt-6 space-y-4">
            <h4 className="font-medium">{t('multiSymbol.detailedStatistics')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {statsArray
                .filter(stat => stat.hasData)
                .map((stat) => (
                  <Card key={stat.symbol} className="p-4">
                    <div className="font-medium mb-2">{stat.symbol}</div>
                    <div className="space-y-1 text-sm">
                      {Object.entries(stat)
                        .filter(([key, value]) =>
                          key !== 'symbol' &&
                          key !== 'datasetId' &&
                          key !== 'resultCount' &&
                          key !== 'hasData' &&
                          typeof value === 'number'
                        )
                        .slice(0, 5) // Show only first 5 numeric stats
                        .map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-muted-foreground capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="font-mono">
                              {typeof value === 'number' ? value.toFixed(4) : value}
                            </span>
                          </div>
                        ))}
                    </div>
                  </Card>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface ResultsVisualizationProps {
  results: MultiSymbolAnalysisResponse
  viewMode: 'overview' | 'correlations' | 'performance' | 'statistics' | 'raw'
  onViewModeChange: (mode: string) => void
}

export default function ResultsVisualization({
  results,
  viewMode,
  onViewModeChange
}: ResultsVisualizationProps) {
  const { t } = useTranslation()
  const { metadata, results: rawResults, symbolStats, crossSymbolInsights } = results

  return (
    <div className="space-y-6">
      {/* Results Summary */}
      <Card>
        <CardHeader>
          <CardTitle>{t('multiSymbol.analysisResults')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{metadata.totalSymbols}</div>
              <div className="text-sm text-muted-foreground">{t('multiSymbol.totalSymbols')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{metadata.symbolsAnalyzed}</div>
              <div className="text-sm text-muted-foreground">{t('multiSymbol.analyzed')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{metadata.executionTimeMs}ms</div>
              <div className="text-sm text-muted-foreground">{t('multiSymbol.executionTime')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{rawResults.length}</div>
              <div className="text-sm text-muted-foreground">{t('multiSymbol.resultRows')}</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant="outline" className="capitalize">
              {metadata.analysisType} Analysis
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={onViewModeChange}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">{t('multiSymbol.tabOverview')}</TabsTrigger>
          <TabsTrigger value="correlations" disabled={!crossSymbolInsights.correlations}>
            {t('multiSymbol.tabCorrelations')}
          </TabsTrigger>
          <TabsTrigger value="performance" disabled={!crossSymbolInsights.bestPerformers}>
            {t('multiSymbol.tabPerformance')}
          </TabsTrigger>
          <TabsTrigger value="statistics">{t('multiSymbol.tabStatistics')}</TabsTrigger>
          <TabsTrigger value="raw">{t('multiSymbol.tabRawData')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>{t('multiSymbol.analysisSummary')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{t('multiSymbol.dataCoverage')}</span>
                    <span>{Math.round((metadata.symbolsAnalyzed / metadata.totalSymbols) * 100)}%</span>
                  </div>
                  <Progress
                    value={(metadata.symbolsAnalyzed / metadata.totalSymbols) * 100}
                    className="h-2"
                  />
                </div>

                {crossSymbolInsights.correlations && (
                  <div className="text-sm">
                    <span className="font-medium">{t('multiSymbol.correlationsFound')}:</span>{' '}
                    {crossSymbolInsights.correlations.length}
                  </div>
                )}

                {crossSymbolInsights.bestPerformers && (
                  <div className="text-sm">
                    <span className="font-medium">{t('multiSymbol.performanceMetrics')}:</span>{' '}
                    {new Set(crossSymbolInsights.bestPerformers.map(p => p.metric)).size}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cross-symbol Insights */}
            {crossSymbolInsights.patterns && crossSymbolInsights.patterns.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('multiSymbol.patternsDetected')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {crossSymbolInsights.patterns.map((pattern, index) => (
                      <li key={index} className="text-sm flex items-start gap-2">
                        <span className="text-muted-foreground">•</span>
                        <span>{pattern}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="correlations">
          {crossSymbolInsights.correlations ? (
            <CorrelationHeatmap correlations={crossSymbolInsights.correlations} />
          ) : (
            <Card>
              <CardContent className="text-center py-8 text-muted-foreground">
                {t('multiSymbol.noCorrelationData')}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="performance">
          {crossSymbolInsights.bestPerformers ? (
            <PerformanceRanking bestPerformers={crossSymbolInsights.bestPerformers} />
          ) : (
            <Card>
              <CardContent className="text-center py-8 text-muted-foreground">
                {t('multiSymbol.noPerformanceData')}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="statistics">
          <SymbolStatistics symbolStats={symbolStats} metadata={metadata} />
        </TabsContent>

        <TabsContent value="raw">
          <Card>
            <CardHeader>
              <CardTitle>{t('multiSymbol.rawResults')}</CardTitle>
              <div className="text-sm text-muted-foreground">
                {t('multiSymbol.rowsReturned', { count: rawResults.length })}
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-auto">
                <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <div className="min-w-full">
                    <div className="grid gap-4 p-4 font-medium border-b bg-muted" style={{
                      gridTemplateColumns: rawResults.length > 0 ? `repeat(${Object.keys(rawResults[0]).length}, minmax(100px, 1fr))` : '1fr'
                    }}>
                      {rawResults.length > 0 && Object.keys(rawResults[0]).map(key => (
                        <div key={key} className="text-sm">{key}</div>
                      ))}
                    </div>
                    {rawResults.slice(0, 100).map((row, index) => (
                      <div key={index} className="grid gap-4 p-4 border-b" style={{
                        gridTemplateColumns: `repeat(${Object.values(row).length}, minmax(100px, 1fr))`
                      }}>
                        {Object.values(row).map((value, cellIndex) => (
                          <div key={cellIndex} className="font-mono text-xs">
                            {typeof value === 'number' ? value.toFixed(4) : String(value)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
                {rawResults.length > 100 && (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    {t('multiSymbol.showingFirstRows', { total: rawResults.length })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}